import type { StageItem } from "@/features/jobs/components/stage-cards";
import {
  DEFAULT_ARCHIVED_STAGE_DESCRIPTION,
  DEFAULT_INBOX_STAGE_DESCRIPTION,
  DEFAULT_SOURCED_STAGE_DESCRIPTION,
  getDefaultStageDescription,
} from "@/features/jobs/lib/default-stage-descriptions";

export interface StagePresetItem {
  name: string;
  color: string;
  description: string;
  sort_order: number;
}

const DEFAULT_BOOKEND_LEADING: StageItem = {
  id: "sourced",
  name: "Sourced",
  color: "#2B5DF3",
  description: DEFAULT_SOURCED_STAGE_DESCRIPTION,
  is_system: true,
};

const DEFAULT_BOOKEND_TRAILING: StageItem = {
  id: "archived",
  name: "Archived",
  color: "#F84043",
  description: DEFAULT_ARCHIVED_STAGE_DESCRIPTION,
  is_system: true,
};

const DEFAULT_MIDDLE_STAGE: StageItem = {
  id: "inbox",
  name: "Inbox",
  color: "#63D47A",
  description: DEFAULT_INBOX_STAGE_DESCRIPTION,
  is_system: false,
};

function withDefaultStageDescription(stage: StageItem): StageItem {
  if (stage.description.trim()) return stage;
  const description = getDefaultStageDescription(stage.name);
  return description ? { ...stage, description } : stage;
}

export function buildDefaultStagePresetStages(): StageItem[] {
  return [DEFAULT_BOOKEND_LEADING, DEFAULT_MIDDLE_STAGE, DEFAULT_BOOKEND_TRAILING];
}

export function parseStagePresetItems(
  stagesJson?: string | null,
): StagePresetItem[] | undefined {
  if (!stagesJson) return undefined;
  try {
    const parsed = JSON.parse(stagesJson) as unknown;
    if (!Array.isArray(parsed)) return undefined;
    return parsed
      .filter(
        (item): item is StagePresetItem =>
          !!item &&
          typeof item === "object" &&
          typeof (item as StagePresetItem).name === "string" &&
          typeof (item as StagePresetItem).color === "string",
      )
      .map((item, index) => ({
        name: item.name,
        color: item.color,
        description: item.description ?? "",
        sort_order:
          typeof item.sort_order === "number" ? item.sort_order : index,
      }))
      .sort((a, b) => a.sort_order - b.sort_order);
  } catch {
    return undefined;
  }
}

export function stagePresetItemsToFormStages(items: StagePresetItem[]): StageItem[] {
  const middle = items.map((item, index) => ({
    id: `preset-${index}-${item.name.toLowerCase().replace(/\s+/g, "-")}`,
    name: item.name,
    color: item.color,
    description:
      item.description?.trim() || getDefaultStageDescription(item.name) || "",
    is_system: false,
  }));
  return [DEFAULT_BOOKEND_LEADING, ...middle, DEFAULT_BOOKEND_TRAILING];
}

export function serializeStagePresetItems(stages: StageItem[]): StagePresetItem[] {
  const { editable } = splitStagesForPreset(stages);
  return editable.map((stage, index) => ({
    name: stage.name,
    color: stage.color,
    description: stage.description ?? "",
    sort_order: index,
  }));
}

export function splitStagesForPreset(stages: StageItem[]) {
  const archived = stages.filter(
    (stage) => stage.is_system && stage.name.toLowerCase() === "archived",
  );
  const nonArchived = stages.filter(
    (stage) => !(stage.is_system && stage.name.toLowerCase() === "archived"),
  );
  const leading = nonArchived.filter((stage) => stage.is_system);
  const editable = nonArchived.filter((stage) => !stage.is_system);
  return { leading, editable, trailing: archived };
}

export function applyStagePresetToStages(
  currentStages: StageItem[],
  presetItems: StagePresetItem[],
): StageItem[] {
  const { leading, editable, trailing } = splitStagesForPreset(currentStages);
  const existingByName = new Map(
    editable.map((stage) => [stage.name.trim().toLowerCase(), stage] as const),
  );

  const custom = presetItems.map((item, index) => {
    const key = item.name.trim().toLowerCase();
    const existing = existingByName.get(key);
    const description =
      item.description?.trim() ||
      existing?.description?.trim() ||
      getDefaultStageDescription(item.name) ||
      "";

    return {
      id: existing?.id ?? `custom-${Date.now()}-${index}`,
      name: item.name,
      color: item.color,
      description,
      is_system: false,
    };
  });

  return [
    ...leading.map(withDefaultStageDescription),
    ...custom,
    ...trailing.map(withDefaultStageDescription),
  ];
}

export function stagesToPresetFormStages(stages: StageItem[]): StageItem[] {
  const { leading, editable, trailing } = splitStagesForPreset(stages);
  const resolvedLeading =
    leading.length > 0
      ? leading.map(withDefaultStageDescription)
      : [DEFAULT_BOOKEND_LEADING];
  const resolvedTrailing =
    trailing.length > 0
      ? trailing.map(withDefaultStageDescription)
      : [DEFAULT_BOOKEND_TRAILING];
  return [
    ...resolvedLeading,
    ...editable.map(withDefaultStageDescription),
    ...resolvedTrailing,
  ];
}
